import { numberToUserDisplay } from '@ebiz/pretty-num';
import { SensorData } from '../models/sensor';
import ModelService from '../services/model';
import PersistenceService from '../services/persistence';
import PDFReport from '@ebiz/aditools-design-report';
import type { PdfImageData, PdfTableData } from '@ebiz/aditools-design-report';
import type IntlService from 'ember-intl/services/intl';

// import OpampService from '../services/opamp';
// import SensorService from '../services/sensor';
import ENV from '../config/environment';
import { GraphLine, GraphPoint } from '@ebiz/ember-aditools-graph';

const ROOT_URL = window.location.origin;
const COVER_IMAGE_RESOURCE = `${ENV.rootURL}assets/images/PDFDesignFrontPage.jpg`;

interface PDFStructure {
  dictionary: Array<{ name: string; value: string }>;
  applicationDisplayName: string;
  userName: string;
  heading: string;
  subheading: string;
  fields: Array<{ name: string; value: string }>;
  images: Array<{ name: string; value: string }>;
  designInfo: { name: string; description: string; dataSheetUrl: unknown };
  date: string;
  bomTableData: Array<Array<string>>;
}

interface BOMHash {
  count: number;
  designators: string[];
  value: string;
  package: string;
  material: string;
  tolerance: string;
  prodURL?: string;
}

let cleanImage = function (image: string): string {
  let stringToRemove = 'data:image/png;base64,';
  let imageString = image.toString();
  imageString = imageString.replace(stringToRemove, '');
  return imageString;
};

let generatePDFTitlePageDescription = function (
  designName: string,
  userNameString: string,
  pdfDate: string,
  intl: IntlService,
): string {
  let titlePageText = '';
  if (isNullOrWhiteSpace(designName) && isNullOrWhiteSpace(userNameString)) {
    // Username and design missing.
    titlePageText = intl.t('download.pdf-content.pdf-desc-no-name-desc', { date: pdfDate }).toString();
  } else if (isNullOrWhiteSpace(designName) && !isNullOrWhiteSpace(userNameString)) {
    // Design name missing and username is present.
    titlePageText = intl
      .t('download.pdf-content.pdf-desc-no-design-name', { user: userNameString.trim(), date: pdfDate })
      .toString();
  } else if (isNullOrWhiteSpace(userNameString) && !isNullOrWhiteSpace(designName)) {
    // Username name missing and design name is present.
    titlePageText = intl
      .t('download.pdf-content.pdf-desc-no-user-name', { desc: designName.trim(), date: pdfDate })
      .toString();
  } else {
    // Username and design presnt.
    titlePageText = intl
      .t('download.pdf-content.pdf-desc', { desc: designName.trim(), user: userNameString.trim(), date: pdfDate })
      .toString();
  }
  return titlePageText;
};

let isNullOrWhiteSpace = function (str: string): boolean {
  // Check if the string is undefined.
  if (str)
    // Check if the string is empty, null or white space.
    return str === null || str.match(/^ *$/) !== null;
  else return true;
};

let bomForPDF = function (bom: Array<BOMHash>, intl: IntlService): Array<Array<string>> {
  let bomArray: Array<Array<string>> = [];
  bomArray.push([
    intl.t('figures.bom.quantity').toString(),
    intl.t('figures.bom.designator').toString(),
    intl.t('figures.bom.value').toString(),
    intl.t('figures.bom.package').toString(),
    intl.t('figures.bom.material').toString(),
    intl.t('figures.bom.tolerance').toString(),
  ]);
  for (const item of bom) {
    bomArray.push([
      item.count.toString(),
      item.designators.join(),
      item.value,
      item.package,
      item.material,
      item.tolerance,
    ]);
  }
  return bomArray;
};

let getFullSensorName = function (sensor: string, sensorList?: Array<SensorData>): string {
  let sensorName = '';
  if (sensorList && sensor.length > 0) {
    const sensorData = sensorList.find((item) => item.partNumber === sensor);
    if (sensorData) {
      sensorName = sensorData.manufacturer + ' ' + sensorData.partNumber;
    }
  }
  return sensorName;
};

let createCsvData = function (headers: string[], data: GraphLine[], columnSelectors: number[][]): string[][] {
  let rows = [];

  // Lay out the header row
  let headerdata = [];
  for (let header of headers) {
    headerdata.push(header);
  }
  rows.push(headerdata);

  // Now lay out the numeric rows
  let count = data[0] ? data[0].length : 0;
  // walk the rows
  for (let rowIndex = 0; rowIndex < count; rowIndex++) {
    let row = [];
    // walk the columns
    for (let columnSelector of columnSelectors) {
      let dataGraphIndex = data[columnSelector[0] as number] as GraphLine;
      let dataGraphRowIndex = dataGraphIndex[rowIndex] as GraphPoint;
      let cellValue = dataGraphRowIndex[columnSelector[1] as number];
      if (cellValue) {
        row.push(cellValue.toString());
      }
    }
    rows.push(row);
  }
  return rows;
};

export function buildDesignFile(persistence: PersistenceService): DownloadFile {
  let data = persistence.getDesign();
  let now = new Date();
  let ticks = now.getTime();
  let name = 'SavedDesignPD' + ticks.toString() + '.json';
  let description = 'Photodiode Design';
  let saveDateStamp =
    now.getFullYear() +
    '-' +
    ('0' + (now.getMonth() + 1)).slice(-2) +
    '-' +
    ('0' + now.getDate()).slice(-2) +
    ' ' +
    ('0' + (((now.getHours() + 11) % 12) + 1)).slice(-2) +
    ':' +
    ('0' + now.getMinutes()).slice(-2) +
    ':' +
    ('0' + now.getSeconds()).slice(-2) +
    ' ' +
    (now.getHours() >= 12 ? 'PM' : 'AM');
  let toolName = 'PD';
  let jsonVersion = '1.0.0';
  let designData = {
    Name: name,
    Description: description,
    Tool: toolName,
    Design: data,
    CreatedDate: saveDateStamp,
    UpdatedDate: saveDateStamp,
    JsonVersion: jsonVersion,
  };
  let jsonStringData = JSON.stringify(designData, null, '\t');
  return {
    name: 'design.json',
    value: jsonStringData,
    location: '',
    type: 'DESIGNJSON',
  };
}

export function buildPDFData(
  model: ModelService,
  imageList: Array<PDFImageInfo>,
  name: string,
  desc: string,
  intl: IntlService,
): Array<DownloadFile> {
  let filesToUpload = [];
  let now = new Date();
  let saveDateStamp =
    ('0' + (now.getMonth() + 1)).slice(-2) + '/' + ('0' + now.getDate()).slice(-2) + '/' + now.getFullYear();
  let designName = name && name.trim().length > 0 ? name : 'Photodiode Design';
  let designDesc = desc && desc.trim().length > 0 ? desc : 'Photodiode Design';
  let pdfData: PDFStructure = {
    dictionary: [
      { name: 'designDescription', value: generatePDFTitlePageDescription(designName, '', saveDateStamp, intl) },
      { name: 'ieImageMissing', value: intl.t('download.pdf-content.pdf-data-ie-image-missing').toString() },
      { name: 'bomPageTitle', value: intl.t('download.pdf-content.pdf-page-headers.bill-of-materials').toString() },
    ],
    applicationDisplayName: intl.t('download.pdf-content.pdf-tool-name').toString(),
    userName: ' ',
    heading: intl.t('download.pdf-content.pdf-heading').toString(),
    subheading: intl.t('download.pdf-content.pdf-subheading').toString(),
    fields: [
      {
        name: intl.t('download.pdf-content.pdf-data-fields.reverse-voltage').toString(),
        value:
          numberToUserDisplay(model.state.Vr, 'Engineering', 3, { showZeroes: false, supplySpace: true }) +
          intl.t('units.V').toString(),
      },
      {
        name: intl.t('download.pdf-content.pdf-data-fields.photodiode-bias').toString(),
        value:
          model.state.Bias === '+'
            ? intl.t('settings.photodiode.reverse-voltage-positive').toString()
            : intl.t('settings.photodiode.reverse-voltage-negative').toString(),
      },
      {
        name: intl.t('download.pdf-content.pdf-data-fields.capacitance').toString(),
        value:
          numberToUserDisplay(model.state.Cd, 'Engineering', 3, { showZeroes: false, supplySpace: true }) +
          intl.t('units.F').toString(),
      },
      {
        name: intl.t('download.pdf-content.pdf-data-fields.shunt-resistance').toString(),
        value:
          numberToUserDisplay(model.state.Rsh, 'Engineering', 3, { showZeroes: false, supplySpace: true }) +
          intl.t('units.R').toString(),
      },
      {
        name: intl.t('download.pdf-content.pdf-data-fields.peak-current').toString(),
        value:
          numberToUserDisplay(model.state.Ip, 'Engineering', 3, { showZeroes: false, supplySpace: true }) +
          intl.t('units.A').toString(),
      },
      {
        name: intl.t('units.Q'),
        value: numberToUserDisplay(model.frequencyResponseDb.Q, 'Standard', 3),
      },
      {
        name: intl.t('download.pdf-content.pdf-data-fields.peak-voltage').toString(),
        value:
          numberToUserDisplay(model.state.Vp, 'Engineering', 3, { showZeroes: false, supplySpace: true }) +
          intl.t('units.V').toString(),
      },
      {
        name: intl.t('download.pdf-content.pdf-data-fields.requested-bandwidth').toString(),
        value:
          numberToUserDisplay(
            model.state.SpeedPreference === 'PulseWidth' ? 2 / model.state.TargetSpeed : model.state.TargetSpeed,
            'Engineering',
            3,
            { showZeroes: false, supplySpace: true },
          ) + intl.t('units.hertz').toString(),
      },
      {
        name: intl.t('download.pdf-content.pdf-data-fields.sensor').toString(),
        value:
          model.state.Photodiode && model.state.Photodiode !== ''
            ? getFullSensorName(model.state.Photodiode, model.sensors)
            : intl.t('figures.schematic.photodiode-model-custom').toString(),
      },
      {
        name: intl.t('download.pdf-content.pdf-data-fields.stages').toString(),
        value:
          model.state.StageCount === 1
            ? intl.t('download.pdf-content.pdf-data-fields.one-stage').toString()
            : intl.t('download.pdf-content.pdf-data-fields.two-stage').toString(),
      },
    ],
    images: [
      {
        name: intl.t('download.pdf-content.pdf-page-headers.circuit'),
        value: cleanImage(imageList[0] ? imageList[0].data : ''),
      },
      {
        name: intl.t('download.pdf-content.pdf-page-headers.pulse-response'),
        value: cleanImage(imageList[1] ? imageList[1].data : ''),
      },
      {
        name: intl.t('download.pdf-content.pdf-page-headers.frequency-response'),
        value: cleanImage(imageList[2] ? imageList[2].data : ''),
      },
      {
        name: intl.t('download.pdf-content.pdf-page-headers.spectral-noise'),
        value: cleanImage(imageList[3] ? imageList[3].data : ''),
      },
      {
        name: intl.t('download.pdf-content.pdf-page-headers.noise-gain'),
        value: cleanImage(imageList[4] ? imageList[4].data : ''),
      },
      {
        name: intl.t('download.pdf-content.pdf-page-headers.snr'),
        value: cleanImage(imageList[5] ? imageList[5].data : ''),
      },
    ],
    designInfo: { name: designName, description: designDesc, dataSheetUrl: null },
    date: saveDateStamp,
    bomTableData: bomForPDF(model.bom, intl),
  };
  filesToUpload.push({ name: 'Design.pdf', value: pdfData, location: '', type: 'DESIGNPDF' });
  return filesToUpload;
}

export function buildPDFBinaryData(
  model: ModelService,
  images: Array<PDFImageInfo>,
  name: string,
  desc: string,
  fonts: Array<PDFFontInfo>,
  intl: IntlService,
): Promise<Array<DownloadFile>> {
  let filesToUpload: Array<DownloadFile> = [];
  let now = new Date();
  let saveDateStamp =
    ('0' + (now.getMonth() + 1)).slice(-2) + '/' + ('0' + now.getDate()).slice(-2) + '/' + now.getFullYear();
  let designName = name && name.trim().length > 0 ? name : 'Photodiode Design';
  let designDesc = desc && desc.trim().length > 0 ? desc : '';
  let fields: Array<{ name: string; value: string }> = [
    {
      name: intl.t('download.pdf-content.pdf-data-fields.reverse-voltage').toString(),
      value:
        numberToUserDisplay(model.state.Vr, 'Engineering', 3, { showZeroes: false, supplySpace: true }) +
        intl.t('units.V').toString(),
    },
    {
      name: intl.t('download.pdf-content.pdf-data-fields.photodiode-bias').toString(),
      value:
        model.state.Bias === '+'
          ? intl.t('settings.photodiode.reverse-voltage-positive').toString()
          : intl.t('settings.photodiode.reverse-voltage-negative').toString(),
    },
    {
      name: intl.t('download.pdf-content.pdf-data-fields.capacitance').toString(),
      value:
        numberToUserDisplay(model.state.Cd, 'Engineering', 3, { showZeroes: false, supplySpace: true }) +
        intl.t('units.F').toString(),
    },
    {
      name: intl.t('download.pdf-content.pdf-data-fields.shunt-resistance').toString(),
      value:
        numberToUserDisplay(model.state.Rsh, 'Engineering', 3, { showZeroes: false, supplySpace: true }) +
        intl.t('units.R').toString(),
    },
    {
      name: intl.t('download.pdf-content.pdf-data-fields.peak-current').toString(),
      value:
        numberToUserDisplay(model.state.Ip, 'Engineering', 3, { showZeroes: false, supplySpace: true }) +
        intl.t('units.A').toString(),
    },
    {
      name: intl.t('units.Q'),
      value: numberToUserDisplay(model.frequencyResponseDb.Q, 'Standard', 3),
    },
    {
      name: intl.t('download.pdf-content.pdf-data-fields.peak-voltage').toString(),
      value:
        numberToUserDisplay(model.state.Vp, 'Engineering', 3, { showZeroes: false, supplySpace: true }) +
        intl.t('units.V').toString(),
    },
    {
      name: intl.t('download.pdf-content.pdf-data-fields.requested-bandwidth').toString(),
      value:
        numberToUserDisplay(
          model.state.SpeedPreference === 'PulseWidth' ? 2 / model.state.TargetSpeed : model.state.TargetSpeed,
          'Engineering',
          3,
          { showZeroes: false, supplySpace: true },
        ) + intl.t('units.hertz').toString(),
    },
    {
      name: intl.t('download.pdf-content.pdf-data-fields.sensor').toString(),
      value:
        model.state.Photodiode && model.state.Photodiode !== ''
          ? getFullSensorName(model.state.Photodiode, model.sensors)
          : intl.t('figures.schematic.photodiode-model-custom').toString(),
    },
    {
      name: intl.t('download.pdf-content.pdf-data-fields.stages').toString(),
      value:
        model.state.StageCount === 1
          ? intl.t('download.pdf-content.pdf-data-fields.one-stage').toString()
          : intl.t('download.pdf-content.pdf-data-fields.two-stage').toString(),
    },
  ];

  let imagesList: Array<PdfImageData> = [
    {
      name: intl.t('download.pdf-content.pdf-page-headers.circuit'),
      value: cleanImage(images[0] ? images[0].data : ''),
      width: images[0] ? images[0].width : 0,
      height: images[0] ? images[0].height : 0,
      format: images[0] ? images[0].format : '',
    },
    {
      name: intl.t('download.pdf-content.pdf-page-headers.pulse-response'),
      value: cleanImage(images[1] ? images[1].data : ''),
      width: images[1] ? images[1].width : 0,
      height: images[1] ? images[1].height : 0,
      format: images[1] ? images[1].format : '',
    },
    {
      name: intl.t('download.pdf-content.pdf-page-headers.frequency-response'),
      value: cleanImage(images[2] ? images[2].data : ''),
      width: images[2] ? images[2].width : 0,
      height: images[2] ? images[2].height : 0,
      format: images[2] ? images[2].format : '',
    },
    {
      name: intl.t('download.pdf-content.pdf-page-headers.spectral-noise'),
      value: cleanImage(images[3] ? images[3].data : ''),
      width: images[3] ? images[3].width : 0,
      height: images[3] ? images[3].height : 0,
      format: images[3] ? images[3].format : '',
    },
    {
      name: intl.t('download.pdf-content.pdf-page-headers.noise-gain'),
      value: cleanImage(images[4] ? images[4].data : ''),
      width: images[4] ? images[4].width : 0,
      height: images[4] ? images[4].height : 0,
      format: images[4] ? images[4].format : '',
    },
    {
      name: intl.t('download.pdf-content.pdf-page-headers.snr'),
      value: cleanImage(images[5] ? images[5].data : ''),
      width: images[5] ? images[5].width : 0,
      height: images[5] ? images[5].height : 0,
      format: images[5] ? images[5].format : '',
    },
  ];

  let bomTable = bomForPDF(model.bom, intl);
  let bomData: PdfTableData = {
    title: intl.t('download.pdf-content.pdf-page-headers.bill-of-materials').toString(),
    columns: bomTable[0] as string[],
    columnWidths: [0.1, 0.29, 0.25, 0.12, 0.12, 0.12],
    data: bomTable.slice(1),
  };

  return new Promise((resolve, reject) => {
    let coverImage = `${ROOT_URL}/${COVER_IMAGE_RESOURCE.replace(/^\/+/g, '')}`;
    return fetch(coverImage, { headers: { 'Cache-Control': 'no-store' }, method: 'GET' })
      .then((response) => {
        if (response.status !== 200) {
          throw `HTTP Request Failed: ${response.status} ${response.statusText}`;
        }
        return response.blob();
      })
      .then((content) => {
        // 1. Read the Cover Image that will be used in the PDF
        return new Promise((resolve, reject) => {
          let reader = new FileReader();
          reader.readAsDataURL(content);
          reader.onloadend = function () {
            let coverImageData = reader.result;
            if (coverImageData !== null) {
              resolve(coverImageData);
            } else {
              reject(coverImageData);
            }
          };
        });
      })
      .then((coverImageData: unknown) => {
        // 2. Create new Design Report Object
        let pdfReport = new PDFReport(fonts);

        // 3. Add Title Page
        pdfReport.addTitlePage(
          intl.t('download.pdf-content.pdf-tool-name').toString(),
          generatePDFTitlePageDescription(designName, designDesc, saveDateStamp, intl),
          { data: atob((coverImageData as string).replace('data:image/jpeg;base64,', '')), format: 'JPEG' },
        );

        // 4. Add Data Page
        pdfReport.addDataPage(
          intl.t('download.pdf-content.pdf-heading').toString(),
          intl.t('download.pdf-content.pdf-subheading').toString(),
          fields,
        );

        // 5. Add Image Pages
        for (const [index, image] of imagesList.entries()) {
          pdfReport.addImagePage(image);

          // 6. Same hack as the API, where the BOM comes after the image
          if (index === 0) {
            pdfReport.addTablePage(bomData);
          }
        }

        // Read Generated PDF File and convert to Base-64 String
        let reader = new FileReader();
        reader.readAsDataURL(pdfReport.toBlob() as Blob);
        reader.onloadend = function () {
          let pdfData = reader.result;
          if (pdfData) {
            pdfData = (pdfData as string).replace('data:application/pdf;base64,', '');
            filesToUpload.push({ name: 'Design.pdf', value: pdfData, location: '', type: 'BINARY' });
            resolve(filesToUpload);
          } else {
            reject(filesToUpload);
          }
        };
      });
  });
}

export function buildBOMData(model: ModelService, intl: IntlService): DownloadFile {
  let bomDownloadData = [];
  let bomData = model.bom;
  for (const bomItem of bomData) {
    let bomItemCopy = Object.assign({}, bomItem);
    let value = '';
    if (bomItemCopy.value !== '') {
      value = bomItemCopy.value.replace('Ω', ' ohm'); // replace Ω with ohm
    }
    if (bomItemCopy.package !== '') {
      let pkg = bomItemCopy.package;
      // Change Package Details to Conform to Locale Strings on Extra Parts
      if (pkg.includes('{{header}}')) {
        pkg = pkg.replace('{{header}}', intl.t('figures.bom.extra-components.header').toString());
      } else if (pkg.includes('{{socket}}')) {
        pkg = pkg.replace('{{socket}}', intl.t('figures.bom.extra-components.socket').toString());
      } else if (pkg.includes('{{jumper}}')) {
        pkg = pkg.replace('{{jumper}}', intl.t('figures.bom.extra-components.jumper').toString());
      } else if (pkg.includes('{{test-pin}}')) {
        pkg = pkg.replace('{{test-pin}}', intl.t('figures.bom.extra-components.test-pin').toString());
      } else if (pkg.includes('{{sma-jack}}')) {
        pkg = pkg.replace('{{sma-jack}}', intl.t('figures.bom.extra-components.sma-jack').toString());
      } else if (pkg.includes('{{sma-male}}')) {
        pkg = pkg.replace('{{sma-male}}', intl.t('figures.bom.extra-components.sma-male').toString());
      }
      value += ', ' + pkg;
    }
    if (bomItemCopy.material !== '') {
      value += ', ' + bomItemCopy.material;
    }
    if (bomItemCopy.tolerance !== '') {
      value += ', ' + bomItemCopy.tolerance;
    }
    bomDownloadData.push({ name: value, quantity: bomItemCopy.count, identifier: bomItemCopy.designators.join() });
  }
  return {
    name: 'BOM.csv',
    value: bomDownloadData,
    location: '',
    type: 'BOM',
  };
}

export function buildDataFiles(model: ModelService, intl: IntlService): Array<DownloadFile> {
  let filesToUpload = [];

  // Frequency Response
  filesToUpload.push(buildFrequencyResponseCSV(model, intl));

  // Pulse Response
  filesToUpload.push(buildPulseResponseCSV(model, intl));

  // Noise Gain
  filesToUpload.push(buildNoiseGainCSV(model, intl));

  // Spectral Noise
  filesToUpload.push(buildSpectralNoiseCSV(model, intl));

  return filesToUpload;
}

function buildFrequencyResponseCSV(model: ModelService, intl: IntlService): DownloadFile {
  return {
    name: intl.t('help.frequency-response.title') + '.csv',
    value: createCsvData(
      [
        intl.t('download.data-files.frequency-response.frequency'),
        intl.t('download.data-files.frequency-response.magnitude'),
      ],
      [model.frequencyResponseVA.graph.data],
      [
        [0, 0],
        [0, 1],
      ],
    ),
    location: 'Data Files',
    type: 'CSV',
  };
}

function buildPulseResponseCSV(model: ModelService, intl: IntlService): DownloadFile {
  return {
    name: intl.t('help.pulse-response.title') + '.csv',
    value: createCsvData(
      [
        intl.t('download.data-files.pulse-response.time-axis'),
        intl.t('download.data-files.pulse-response.pulse-response'),
      ],
      [model.pulseResponse.graph.data],
      [
        [0, 0],
        [0, 1],
      ],
    ),
    location: 'Data Files',
    type: 'CSV',
  };
}

function buildNoiseGainCSV(model: ModelService, intl: IntlService): DownloadFile {
  return {
    name: intl.t('help.noise-gain.title') + '.csv',
    value: createCsvData(
      [
        intl.t('download.data-files.noise-gain.frequency'),
        intl.t('download.data-files.noise-gain.noise-gain'),
        intl.t('download.data-files.noise-gain.caps'),
        intl.t('download.data-files.noise-gain.opamp-gain'),
        intl.t('download.data-files.noise-gain.zero'),
      ],
      model.noiseGainVV.graph.data,
      [
        [0, 0],
        [0, 1],
        [1, 1],
        [2, 1],
        [3, 1],
      ],
    ),
    location: 'Data Files',
    type: 'CSV',
  };
}

function buildSpectralNoiseCSV(model: ModelService, intl: IntlService): DownloadFile {
  let titles1 = [
    intl.t('download.data-files.spectral-noise.frequency'),
    intl.t('download.data-files.spectral-noise.total'),
    intl.t('download.data-files.spectral-noise.opv'),
    intl.t('download.data-files.spectral-noise.opi'),
    intl.t('download.data-files.spectral-noise.rf'),
    intl.t('download.data-files.spectral-noise.rin'),
  ];
  let titles2 = [
    intl.t('download.data-files.spectral-noise.frequency'),
    intl.t('download.data-files.spectral-noise.total'),
    intl.t('download.data-files.spectral-noise.opv'),
    intl.t('download.data-files.spectral-noise.opi'),
    intl.t('download.data-files.spectral-noise.rf'),
    intl.t('download.data-files.spectral-noise.rin'),
    intl.t('download.data-files.spectral-noise.opv2'),
    intl.t('download.data-files.spectral-noise.opi2'),
    intl.t('download.data-files.spectral-noise.rfilter'),
  ];
  let arrays1 = [
    [0, 0],
    [0, 1],
    [1, 1],
    [2, 1],
    [3, 1],
    [4, 1],
  ];
  let arrays2 = [
    [5, 1],
    [6, 1],
    [7, 1],
  ];
  let oneStage = model.state.StageCount == 1 ? true : false;
  let titles = oneStage ? titles1 : titles2;
  let arrays = oneStage ? arrays1 : arrays1.concat(arrays2);
  return {
    name: intl.t('help.spectral-noise-density.title') + '.csv',
    value: createCsvData(titles, model.spectralNoise.graph.data, arrays),
    location: 'Data Files',
    type: 'CSV',
  };
}
